import { Ticket } from 'lucide-react'
import { formatPriceMinor } from '../../../lib/formatPriceMinor'
import type { PublicEventPrice } from './publicEventPrice'

type EventPriceRowLabels = {
    free: string
    passesOnly: string
    lowest30d: (price: string) => string
    firstEntry: (price: string) => string
    /** Read before the price by screen readers, e.g. "Cena: ". */
    srPrefix?: string
}

/**
 * The entry price; a reduction everyone gets shows the struck-through Omnibus reference and its note (ADR 0015).
 * Copy comes in as props so the provider-free embed can render it too.
 */
export function EventPriceRow({
    price,
    compact,
    locale,
    labels,
    emphasis = false,
}: {
    price: PublicEventPrice
    compact: boolean
    locale: string
    labels: EventPriceRowLabels
    /** The embed's Event cards keep their bold, dark price without the ticket icon. */
    emphasis?: boolean
}) {
    const textSize = compact ? 'text-[12px]' : 'text-[14px]'
    const textTone = emphasis ? 'font-bold text-slate-900' : 'text-[var(--landing-neutral-500)]'
    const lowest =
        price.kind === 'paid' && price.lowestPrice30dMinor !== null
            ? formatPriceMinor(price.lowestPrice30dMinor, price.currency, locale)
            : null
    const firstEntry =
        price.kind === 'paid' && price.firstEntryMinor !== null
            ? formatPriceMinor(price.firstEntryMinor, price.currency, locale)
            : null
    const srPrefix = labels.srPrefix ? <span className="sr-only">{labels.srPrefix}</span> : null

    return (
        <div className={`flex min-w-0 items-start gap-2 ${emphasis ? '' : 'pl-1'}`}>
            {emphasis ? null : <Ticket
                aria-hidden="true"
                className={`${compact ? 'h-[17px] w-[17px]' : 'h-[18px] w-[18px]'} shrink-0 text-[var(--landing-primary-500)]`}
                strokeWidth={2.25}
            />}
            <div className="min-w-0 leading-[1.2]">
                {price.kind === 'paid' ? (
                    <p className={`${textSize} tabular-nums ${textTone}`}>
                        {srPrefix}
                        <span>{formatPriceMinor(price.amountMinor, price.currency, locale)}</span>
                        {lowest ? <s className="ml-1.5 font-normal text-[var(--landing-neutral-400)]">{lowest}</s> : null}
                    </p>
                ) : (
                    <p className={`${textSize} ${textTone}`}>
                        {srPrefix}
                        <span>{price.kind === 'free' ? labels.free : labels.passesOnly}</span>
                    </p>
                )}
                {lowest ? (
                    <p className="mt-0.5 text-[10px] leading-[1.25] text-[var(--landing-neutral-400)]">{labels.lowest30d(lowest)}</p>
                ) : null}
                {firstEntry ? (
                    <p className={`mt-0.5 ${compact ? 'text-[11px]' : 'text-[12px]'} font-semibold leading-[1.25] text-[var(--landing-primary-600)]`}>{labels.firstEntry(firstEntry)}</p>
                ) : null}
            </div>
        </div>
    )
}
